/**
 * 极练 Rehab & Fit —— 杠铃片与哑铃配重计算器（阶段 3c，纯函数、零依赖）。
 *
 * 理论与算法：
 * 1. 杠铃配重算法：
 *    - 单侧目标做功重量 = (目标总重量 - 空杆重) / 2
 *    - 贪心算法选择每侧标准片（默认 [25, 20, 15, 10, 5, 2.5, 1.25] kg）
 *    - 边界处理：目标重量 ≤ 空杆重时，返回空杆（0片），不报错；
 *    - 无法精确配平时，返回最接近且不超过目标重量的组合，并给出差额与提示。
 * 2. 哑铃配重建议：
 *    - 常见哑铃以 2.5kg 为标准档位（如 10, 12.5, 15, 17.5...）；
 *    - 若输入非标准档位，给出就近上下档位建议。
 */

export interface PlateCalculationInput {
  targetWeightKg: number;
  barbellWeightKg?: number; // 默认 20kg
  availablePlatesPerSide?: number[]; // 默认每侧标准片降序
}

export interface PlateItem {
  plateKg: number;
  count: number;
}

export interface PlateResult {
  exact: boolean;
  barbellWeightKg: number;
  targetWeightKg: number;
  achievedWeightKg: number;
  differenceKg: number;
  platesPerSide: PlateItem[];
  platesPerSideFormatted: string;
  summaryZh: string;
}

export const DEFAULT_BARBELL_WEIGHT_KG = 20;
export const DEFAULT_PLATES_PER_SIDE = [25, 20, 15, 10, 5, 2.5, 1.25];

/**
 * 贪心计算单侧杠铃片组合
 */
export function calculateBarbellPlates(input: PlateCalculationInput): PlateResult {
  const barbellWeightKg = input.barbellWeightKg ?? DEFAULT_BARBELL_WEIGHT_KG;
  const targetWeightKg = Math.max(0, Math.round(input.targetWeightKg * 100) / 100);
  const availablePlates = [...(input.availablePlatesPerSide ?? DEFAULT_PLATES_PER_SIDE)].sort((a, b) => b - a);

  // 1. 如果目标重量小于或等于空杆重量
  if (targetWeightKg <= barbellWeightKg) {
    return {
      exact: targetWeightKg === barbellWeightKg,
      barbellWeightKg,
      targetWeightKg,
      achievedWeightKg: barbellWeightKg,
      differenceKg: Math.round((targetWeightKg - barbellWeightKg) * 100) / 100,
      platesPerSide: [],
      platesPerSideFormatted: "空杆（无需加片）",
      summaryZh: `空杆 ${barbellWeightKg}kg（无需加片）`,
    };
  }

  // 2. 计算每侧需要配的重量
  const totalPlateWeightNeeded = targetWeightKg - barbellWeightKg;
  const sideWeightNeeded = totalPlateWeightNeeded / 2;

  const platesPerSide: PlateItem[] = [];
  let currentAchievedSideWeight = 0;

  for (const plate of availablePlates) {
    if (plate <= 0) continue;
    // 计算当前片可放的最大数量（允许无限对，贪心装填）
    const count = Math.floor((sideWeightNeeded - currentAchievedSideWeight + 0.001) / plate);
    if (count > 0) {
      platesPerSide.push({ plateKg: plate, count });
      currentAchievedSideWeight += plate * count;
      currentAchievedSideWeight = Math.round(currentAchievedSideWeight * 1000) / 1000;
    }
  }

  const achievedWeightKg = Math.round((barbellWeightKg + currentAchievedSideWeight * 2) * 100) / 100;
  const differenceKg = Math.round((targetWeightKg - achievedWeightKg) * 100) / 100;
  const exact = Math.abs(differenceKg) < 0.01;

  // 格式化输出
  const formattedItems = platesPerSide.map((p) => (p.count === 1 ? `${p.plateKg}kg` : `${p.plateKg}kg×${p.count}`));
  const platesPerSideFormatted = formattedItems.length > 0 ? formattedItems.join(" + ") : "空杆";

  let summaryZh = `单侧：${platesPerSideFormatted}（单侧 ${currentAchievedSideWeight}kg）`;
  if (!exact) {
    summaryZh += ` · 差额 ${Math.abs(differenceKg)}kg（建议微调）`;
  }

  return {
    exact,
    barbellWeightKg,
    targetWeightKg,
    achievedWeightKg,
    differenceKg,
    platesPerSide,
    platesPerSideFormatted,
    summaryZh,
  };
}

export interface DumbbellPlateResult {
  exact: boolean;
  targetWeightKg: number;
  nearestLowerKg: number;
  nearestHigherKg: number;
  suggestedWeightKg: number;
  hintZh: string;
}

/**
 * 哑铃配重建议（标准 2.5kg 递增档位）
 */
export function calculateDumbbellWeight(targetWeightKg: number, step = 2.5): DumbbellPlateResult {
  const target = Math.max(0, Math.round(targetWeightKg * 100) / 100);
  const remainder = target % step;
  const exact = remainder < 0.01 || Math.abs(remainder - step) < 0.01;

  if (exact) {
    return {
      exact: true,
      targetWeightKg: target,
      nearestLowerKg: target,
      nearestHigherKg: target,
      suggestedWeightKg: target,
      hintZh: `标准哑铃档位：每只 ${target} kg`,
    };
  }

  const nearestLowerKg = Math.floor(target / step) * step;
  const nearestHigherKg = nearestLowerKg + step;
  const suggestedWeightKg = target - nearestLowerKg <= nearestHigherKg - target ? nearestLowerKg : nearestHigherKg;

  return {
    exact: false,
    targetWeightKg: target,
    nearestLowerKg,
    nearestHigherKg,
    suggestedWeightKg,
    hintZh: `非标准档位：建议选择 ${nearestLowerKg} kg 或 ${nearestHigherKg} kg 哑铃`,
  };
}
